import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Layout } from "components/Layout";
import { TaskStepProgress } from "components/TaskStepProgress";
import { WorkTaskIcon } from "components/WorkTaskIcon";
import { Modal } from "components/Modal";
import { Button } from "components/Form/Button";
import { AppRoutes } from "types";
import { ReactComponent as Capture } from "assets/capture.svg";
import { ReactComponent as Orb } from "assets/orb.svg";
import { ReactComponent as Gallery } from "assets/gallery.svg";
import { ReactComponent as Iris } from "assets/iris.svg";
import { ReactComponent as PCR } from "assets/pcr.svg";
import { ReactComponent as Videos } from "assets/videos.svg";
import { ReactComponent as Webcam } from "assets/webcam.svg";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { Link } from "components/Link";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { SessionMetadataContext } from "contexts/SessionMetadata/context";
import { captureDB } from "lib/indexed-db";
import { apiCheckReconsent, apiReconsent } from "lib/api";
import { toast } from "react-toastify";
import { PSC } from "components/PCS";
import { TaskNavigationScanner } from "components/TaskNavigationScanner";
import {
  getTaskNavigationRoute,
  getTaskNavigationTask,
  TaskNavigationTarget,
} from "lib/task-navigation-qr";
import { Storage, StorageKey } from "lib/storage";
// import { getCurrentSessionId } from "lib/session";

export const Home = memo(function Home() {
  const [showAgreementMessage, setShowAgreementMessage] =
    useState<boolean>(false);
  const [showReconsent, setShowReconsent] = useState(false);
  const [reconsentLoading, setReconsentLoading] = useState(false);
  const [reconsentAgreed, setReconsentAgreed] = useState(false);
  const { config, contributor } = useContext(CollectionDetailsContext);
  const [navigationReady, setNavigationReady] = useState(false);
  const [navigationCheckFailed, setNavigationCheckFailed] = useState(false);
  const { startTask } = useContext(SessionMetadataContext);
  const navigate = useNavigate();
  const privacyModal = usePrivacyModal();
  const activeScope = useRef({
    config,
    contributor,
    navigationReady,
    showReconsent,
  });
  activeScope.current = { config, contributor, navigationReady, showReconsent };

  const checkReconsent = useCallback(() => {
    setNavigationReady(false);
    setNavigationCheckFailed(false);
    apiCheckReconsent()
      .then((needs) => {
        if (needs) setShowReconsent(true);
        setNavigationReady(true);
      })
      .catch(() => setNavigationCheckFailed(true));
  }, []);
  useEffect(checkReconsent, [checkReconsent]);

  const handleReconsent = useCallback(async () => {
    setReconsentLoading(true);
    try {
      await apiReconsent();
      setShowReconsent(false);
      toast.success("Thank you for confirming your consent.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setReconsentLoading(false);
    }
  }, []);

  const handleTaskClick = useCallback(
    (
      task: {
        task_id: string;
        capture_type: string;
        task_name: string;
        workTaskDocumentId?: string;
      },
      route: AppRoutes,
      taskNavigation?: TaskNavigationTarget
    ) => {
      const documentId = config?.selectedCollection?.id;
      const owner = Storage.get(StorageKey.TOKEN);
      const stillValid = () => {
        if (!taskNavigation) return true;
        try {
          const current = activeScope.current;
          if (
            !current.config ||
            current.config.selectedCollection?.id !== documentId ||
            Storage.get(StorageKey.TOKEN) !== owner ||
            !current.navigationReady ||
            current.showReconsent
          )
            throw new Error(
              "The campaign changed or consent must be reviewed. Please scan again."
            );
          const currentTask = getTaskNavigationTask(
            taskNavigation,
            current.config,
            current.contributor
          );
          if (
            currentTask.workTaskDocumentId !== task.workTaskDocumentId ||
            currentTask.capture_type !== task.capture_type
          )
            throw new Error(
              "This task's configuration changed. Please scan again."
            );
          return true;
        } catch (problem) {
          toast.error(
            problem instanceof Error ? problem.message : "Please scan again."
          );
          return false;
        }
      };
      if (!stillValid()) return;
      privacyModal.reset();
      return privacyModal.show({
        onAccept: () => {
          if (!stillValid()) return;
          startTask(async () => {
            if (!stillValid()) return;
            let hasProgress = false;
            try {
              hasProgress = await captureDB.hasStoredProgress(task.task_id);
            } catch (error) {
              console.error("Failed to check task progress:", error);
            }
            if (!stillValid()) return;
            navigate(AppRoutes.taskInstructions, {
              state: {
                taskId: task.task_id,
                taskName: task.task_name,
                nextRoute: route,
                captureType: task.workTaskDocumentId
                  ? undefined
                  : task.capture_type,
                ...(hasProgress ? { skipToProgress: true } : {}),
                ...(taskNavigation ? { taskNavigation } : {}),
              },
            });
          });
        },
        onDecline: () => setShowAgreementMessage(true),
      });
    },
    [config?.selectedCollection?.id, navigate, privacyModal, startTask]
  );

  const handleScannedTask = useCallback(
    (target: TaskNavigationTarget) => {
      try {
        if (!config) throw new Error("The campaign is unavailable.");
        const task = getTaskNavigationTask(target, config, contributor);
        handleTaskClick(task, getTaskNavigationRoute(task), target);
      } catch (problem) {
        toast.error(
          problem instanceof Error ? problem.message : "Please scan again."
        );
      }
    },
    [config, contributor, handleTaskClick]
  );

  if (config === undefined) {
    return null;
  }

  if (config === null) {
    return (
      <Layout
        showTitle
        showPrivacyModal
        toolbarClassName="justify-center"
        toolbar={
          <div className="flex items-center gap-4">
            <Link to={AppRoutes.welcome} className="text-0f24e2">
              Back home
            </Link>
          </div>
        }
      >
        <div className="h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-858494">
              This collection is not available or has no configuration.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      showTitle
      showPrivacyModal
      toolbarClassName="justify-center"
      toolbar={
        <div className="flex items-center gap-4">
          <Link to={AppRoutes.welcome} className="text-0f24e2">
            Back home
          </Link>
        </div>
      }
    >
      {privacyModal.getModal()}
      <div className="space-y-10">
        <div className="grid justify-items-center text-center space-y-4">
          {(config?.collection_title || config?.collection_icon) && (
            <div className="grid justify-items-center text-center space-y-3">
              {config?.collection_icon && (
                <div className="w-20 h-20 flex items-center justify-center">
                  <img
                    src={config.collection_icon}
                    alt="Collection icon"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              {config?.collection_title && (
                <h2 className="font-bold text-20">{config.collection_title}</h2>
              )}
            </div>
          )}
        </div>
        <div className="space-y-4">
          {config?.collection_intro ? (
            <div
              className="text-left [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: config.collection_intro }}
            />
          ) : (
            <p className="text-left">
              Select the task you want to complete. The circle shows completed
              steps out of all steps in each task.
            </p>
          )}
          {config?.collection_image && (
            <img
              src={config.collection_image}
              alt={config.collection_title || "Collection image"}
              className="w-[70%] max-w-sm mx-auto [image-rendering:pixelated]"
            />
          )}
          {showAgreementMessage && (
            <p className="text-xs text-center">
              Please read and agree to the data collection agreement to continue
            </p>
          )}

          <TaskNavigationScanner
            config={config}
            collectionId={contributor}
            disabled={!navigationReady || showReconsent}
            onSelect={handleScannedTask}
          />
          {config.selectedCollection?.data.enable_task_qr_navigation === true &&
            navigationCheckFailed && (
              <div className="space-y-2 text-sm">
                <p role="alert">
                  Consent requirements could not be checked. Please retry before
                  scanning.
                </p>
                <button
                  type="button"
                  className="text-3461fd"
                  onClick={checkReconsent}
                >
                  Retry consent check
                </button>
              </div>
            )}
          <div className="grid gap-3">
            {(!config?.tasks ||
              Object.entries(config.tasks).filter(([_, task]) => task.enabled)
                .length === 0) && (
              <div className="flex flex-col items-center gap-4 py-10 text-center">
                <p className="text-858494">
                  No tasks are available in this collection.
                </p>
              </div>
            )}
            {config?.tasks &&
              Object.entries(config.tasks)
                .filter(([_, task]) => task.enabled)
                .map(([_, task], index) => {
                  const getRouteAndIcon = (captureType: string) => {
                    switch (captureType) {
                      case "photo":
                        return {
                          route: AppRoutes.capture,
                          icon: <Capture />,
                          label: "Photo",
                        };
                      case "iris":
                        return {
                          route: AppRoutes.irisUpload,
                          icon: <Iris />,
                          label: "Iris",
                        };
                      case "pcr":
                        return {
                          route: AppRoutes.pcrCapture,
                          icon: <PCR />,
                          label: "PCR",
                        };
                      case "file":
                        return {
                          route: AppRoutes.batchUpload,
                          icon: <Gallery />,
                          label: "File",
                        };
                      case "orb":
                        return {
                          route: AppRoutes.orb,
                          icon: <Orb />,
                          label: "Orb",
                        };
                      case "rtms":
                        return {
                          route: AppRoutes.stepRtms,
                          icon: <Webcam />,
                          label: "RTMS",
                        };
                      case "video":
                        return {
                          route: AppRoutes.videoUpload,
                          icon: <Videos />,
                          label: "Video",
                        };
                      default:
                        return {
                          route: AppRoutes.capture,
                          icon: <Capture />,
                          label: "Capture",
                        };
                    }
                  };

                  const isWorkTask = Boolean(task.workTaskDocumentId);
                  const { route, icon, label } = isWorkTask
                    ? {
                        route: AppRoutes.taskInstructions,
                        icon: <WorkTaskIcon src={task.workTaskIconUrl} />,
                        label: "Work task",
                      }
                    : getRouteAndIcon(task.capture_type);

                  return (
                    <button
                      key={task.task_id}
                      onClick={() => handleTaskClick(task, route)}
                      className={clsx(
                        "relative grid gap-4 p-7 rounded-xl text-left transition-colors",
                        "bg-transparent hover:bg-3461fd/5 border border-f4f7ff hover:border-3461fd"
                      )}
                    >
                      {icon}
                      <span className="max-w-[75%] break-words">
                        {task.task_name} ({label})
                      </span>

                      <div className="absolute top-1/2 right-4 -translate-y-1/2">
                        <TaskStepProgress
                          taskId={task.task_id}
                          captureType={task.capture_type}
                          workTaskDocumentId={task.workTaskDocumentId}
                        />
                      </div>
                    </button>
                  );
                })}
          </div>
        </div>
      </div>

      <Modal show={showReconsent} onClose={() => {}}>
        <div className="space-y-6 text-center">
          <div className="space-y-3">
            <h2 className="text-24 font-semibold">Welcome to adulthood</h2>
            <p className="text-858494 text-14">
              You are now 18 and have full control over your account &amp; data.
              Please review and confirm your consent to continue participating
              and allow data retention under the current agreement. If you do
              not confirm, your data will be deleted in accordance with our
              policy.
            </p>
          </div>
          <div className="[&_.ps-contract-body]:!max-h-36 [&_.ps-contract-body]:!overflow-y-auto">
            <PSC
              onValid={() => setReconsentAgreed(true)}
              onInvalid={() => setReconsentAgreed(false)}
              onError={() => setReconsentAgreed(true)}
            />
          </div>
          <Button
            onClick={handleReconsent}
            disabled={reconsentLoading || !reconsentAgreed}
            className="w-full"
          >
            {reconsentLoading ? "Confirming..." : "I confirm and consent"}
          </Button>
        </div>
      </Modal>
    </Layout>
  );
});
